import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CsvFieldType, CsvTaskConfig } from '../../models/task-config.model';
import { TaskService, TaskItem } from '../../services/task.service';
import { FileTransferComponent } from '../file-transfer/file-transfer.component';
import { ToastService } from '../../services/toast.service';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs';
import { ViewSchemaEditorComponent } from '../view-schema-editor/view-schema-editor.component';

@Component({
  selector: 'app-csv-task-config',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FileTransferComponent, ViewSchemaEditorComponent],
  templateUrl: './csv-task-config.component.html',
  styleUrl: './csv-task-config.component.scss',
})
export class CsvTaskConfigComponent implements OnInit {
  private fb = inject(FormBuilder);
  private taskService = inject(TaskService);
  private toastService = inject(ToastService);
  private http = inject(HttpClient);

  protected loading = signal(false);
  protected saving = signal(false);
  protected analyzing = signal(false);
  private fieldIdCounter = 0;

  /** 他コンバータ画面と同一のバケットコード */
  private readonly BUCKET_CODE = 'x93jkd8f-qpz1-mv8w';

  /** テーブル列ヘッダー用の日本語ラベル（Converter 画面から移植） */
  private readonly COLUMN_MAP: Record<string, string> = {
    saveResult: '保存用APIの結果',
    rowCount: '行数',
    newId: '新規ID',
    splitKey: '分割キー',
    savePath: '保存先フルパス',
    saveData: '保存データ',
  };

  protected isExecuting = signal(false);
  protected hasExecuted = signal(false);
  protected isLoadingResult = signal(false);
  protected executeError = signal<string | null>(null);
  protected resultRows = signal<TaskItem[]>([]);
  protected viewMode = signal<'table' | 'raw'>('raw');

  protected readonly fieldTypeOptions: CsvFieldType[] = [
    'int',
    'string',
    'double',
    'float',
    'long',
    'boolean',
  ];

  configForm: FormGroup = this.fb.group({
    wbsTaskId: ['convert-demo-csv-to-json', Validators.required],
    id: ['convert-demo-csv-to-json', Validators.required],
    name: ['野球選手CSVをJSONに変換するタスク', Validators.required],
    wbsType: ['task', Validators.required],
    searchPath: ['katori/test/yakyu.csv', Validators.required],
    httpMethod: ['get', Validators.required],
    contentsType: ['csv', Validators.required],
    headerCount: [0, [Validators.required, Validators.min(0)]],
    viewSchemaJson: [''],
    savePath: ['katori/test/yakyu.json', Validators.required],
    fields: this.fb.array([]),
  });

  ngOnInit(): void {
    this.fetchSavedConfig();
  }

  fetchSavedConfig(): void {
    this.loading.set(true);
    const taskId = this.configForm.get('wbsTaskId')?.value || this.configForm.get('id')?.value;

    this.taskService.getTaskFile(taskId).subscribe({
      next: (res) => {
        // API 応答は JSON / CSV で構造が異なるため any で正規化する
        const config = (res as { data?: unknown }).data as
          | (Record<string, unknown> & { profiles?: { actions?: { converter?: unknown } } })
          | null
          | undefined;
        const converter = config?.profiles?.actions?.converter as
          | {
              target?: { searchPath?: string; httpMethod?: string };
              entitySchema?: {
                contentsType?: string;
                headerCount?: number;
                fields?: { fieldName: string; fieldType?: CsvFieldType }[];
              };
              viewSchema?: unknown;
              saveTarget?: { savePath?: string };
            }
          | undefined;

        if (config && converter?.entitySchema && converter?.saveTarget) {
          this.configForm.patchValue({
            wbsTaskId: taskId,
            id: config['id'] as string,
            name: config['name'] as string,
            wbsType: (config['wbsType'] as string) || 'task',
            searchPath: converter.target?.searchPath || '',
            httpMethod: converter.target?.httpMethod || 'get',
            contentsType: converter.entitySchema?.contentsType || 'csv',
            headerCount: converter.entitySchema?.headerCount ?? 0,
            viewSchemaJson:
              converter.viewSchema && typeof converter.viewSchema === 'object'
                ? JSON.stringify(converter.viewSchema, null, 2)
                : '',
            savePath: converter.saveTarget?.savePath || '',
          });

          this.fields.clear();
          const savedFields = converter.entitySchema?.fields || [];
          if (savedFields.length > 0) {
            savedFields.forEach((field: { fieldName: string; fieldType?: CsvFieldType }) => {
              this.addField(field.fieldName, field.fieldType || 'string');
            });
          }
        } else {
          this.fields.clear();
        }

        this.loading.set(false);
      },
      error: () => {
        this.fields.clear();
        this.loading.set(false);
      },
    });
  }

  get fields(): FormArray {
    return this.configForm.get('fields') as FormArray;
  }

  addField(fieldName = '', fieldType: CsvFieldType = 'string'): void {
    const fieldGroup = this.fb.group({
      _id: [this.fieldIdCounter++],
      fieldName: [fieldName, Validators.required],
      fieldType: [fieldType, Validators.required],
    });
    this.fields.push(fieldGroup);
  }

  removeField(index: number): void {
    this.fields.removeAt(index);
  }

  moveFieldUp(index: number): void {
    if (index <= 0) {
      return;
    }
    const field = this.fields.at(index);
    this.fields.removeAt(index);
    this.fields.insert(index - 1, field);
  }

  moveFieldDown(index: number): void {
    if (index >= this.fields.length - 1) {
      return;
    }
    const field = this.fields.at(index);
    this.fields.removeAt(index);
    this.fields.insert(index + 1, field);
  }

  clearFields(): void {
    if (confirm('全てのフィールド定義を削除しますか？')) {
      this.fields.clear();
    }
  }

  private inferFieldType(value: string | undefined): CsvFieldType {
    if (!value) return 'string';

    const trimmed = value.trim().toLowerCase();
    if (trimmed === 'true' || trimmed === 'false') return 'boolean';

    if (trimmed !== '' && !isNaN(Number(trimmed))) {
      if (/^-?\d+$/.test(trimmed)) {
        return 'int';
      }
      return 'double';
    }

    return 'string';
  }

  analyzeCsvHeaders(): void {
    const searchPath = this.configForm.get('searchPath')?.value;
    const headerCount = this.configForm.get('headerCount')?.value;

    if (!searchPath) {
      this.toastService.error('CSV問い合わせ先パスを入力してください');
      return;
    }

    if (headerCount === 0) {
      const ok = confirm('ヘッダー行数が0に設定されています。1行目を項目名として読み込みますか？');
      if (!ok) return;
    }

    this.analyzing.set(true);

    const isUrl = searchPath.startsWith('http');
    const observable = isUrl
      ? this.http.get(searchPath, { responseType: 'text' })
      : this.taskService.getRawFileContent(searchPath);

    observable.subscribe({
      next: (content: string) => {
        if (!content) {
          this.toastService.error('ファイルが空です');
          this.analyzing.set(false);
          return;
        }

        const lines = content.split(/\r?\n/).filter((line) => line.trim() !== '');
        if (lines.length === 0) {
          this.toastService.error('有効なデータが見つかりませんでした');
          this.analyzing.set(false);
          return;
        }

        const headerLine = lines[0];
        const headers = headerLine.split(',').map((h) => h.trim().replace(/^["']|["']$/g, ''));

        const dataLine = lines.length > 1 ? lines[1] : '';
        const dataValues = dataLine
          ? dataLine.split(',').map((v) => v.trim().replace(/^["']|["']$/g, ''))
          : [];

        if (headers.length > 0) {
          const currentFields = this.fields.value as { fieldName: string }[];
          let addedCount = 0;

          headers.forEach((header, index) => {
            const exists = currentFields.some((f) => f.fieldName === header);
            if (!exists && header) {
              const sampleValue = dataValues[index];
              const inferredType = this.inferFieldType(sampleValue);
              this.addField(header, inferredType);
              addedCount++;
            }
          });

          this.toastService.success(
            `${headers.length} 個の項目を解析しました（新しく ${addedCount} 個追加）`,
          );
        } else {
          this.toastService.error('ヘッダーの解析に失敗しました');
        }
        this.analyzing.set(false);
      },
      error: (err) => {
        console.error('Failed to analyze CSV headers', err);
        this.toastService.error('CSVの取得に失敗しました');
        this.analyzing.set(false);
      },
    });
  }

  onSubmit(): void {
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }

    this.saving.set(true);
    const formValue = this.configForm.getRawValue();

    let viewSchema: unknown = null;
    try {
      if (typeof formValue.viewSchemaJson === 'string' && formValue.viewSchemaJson.trim() !== '') {
        viewSchema = JSON.parse(formValue.viewSchemaJson);
      }
    } catch {
      this.saving.set(false);
      this.toastService.error('View Schema (JSON) の形式が正しくありません');
      return;
    }

    const payload: CsvTaskConfig = {
      id: formValue.id,
      name: formValue.name,
      wbsType: 'task',
      profiles: {
        actions: {
          actionType: 'execute',
          converter: {
            target: {
              searchPath: formValue.searchPath,
              httpMethod: formValue.httpMethod,
            },
            entitySchema: {
              contentsType: 'csv',
              headerCount: Number(formValue.headerCount),
              fields: formValue.fields.map(
                (field: { fieldName: string; fieldType: CsvFieldType }) => ({
                  fieldName: field.fieldName,
                  fieldType: field.fieldType,
                }),
              ),
            },
            ...(viewSchema ? { viewSchema } : {}),
            saveTarget: {
              savePath: formValue.savePath,
            },
          },
        },
      },
    };

    const wbsTaskId = formValue.wbsTaskId || formValue.id;

    this.taskService.postCsvTaskFile(payload, wbsTaskId).subscribe({
      next: () => {
        this.saving.set(false);
        this.toastService.success('CSVタスク設定を保存しました');
      },
      error: (err) => {
        this.saving.set(false);
        console.error('Failed to save CSV task config', err);
        this.toastService.error('CSVタスク設定の保存に失敗しました');
      },
    });
  }

  /** CSV タスク実行後、savePath の JSON を取得して表示する */
  executeTask(): void {
    const formValue = this.configForm.getRawValue();
    const wbsTaskId = formValue.wbsTaskId || formValue.id;

    this.isExecuting.set(true);
    this.executeError.set(null);
    this.hasExecuted.set(true);
    this.isLoadingResult.set(true);
    this.resultRows.set([]);

    this.taskService.runConvertTask(wbsTaskId, this.BUCKET_CODE).subscribe({
      next: (response) => {
        if (response.resultCode !== 0 || !response.data?.savePath) {
          this.executeError.set(response.resultMessage || 'タスクからの保存パス取得に失敗しました。');
          this.isExecuting.set(false);
          this.isLoadingResult.set(false);
          return;
        }

        this.taskService
          .getFileContent(response.data.savePath)
          .pipe(finalize(() => this.isExecuting.set(false)))
          .subscribe({
            next: (data) => {
              this.resultRows.set(data);
              this.isLoadingResult.set(false);
            },
            error: (err) => {
              console.error('File fetch error:', err);
              this.executeError.set('変換結果ファイルの取得に失敗しました。');
              this.isLoadingResult.set(false);
            },
          });
      },
      error: (err) => {
        console.error('Task error:', err);
        this.executeError.set('タスクの実行に失敗しました。接続を確認してください。');
        this.isExecuting.set(false);
        this.isLoadingResult.set(false);
      },
    });
  }

  getColumns(): string[] {
    const rows = this.resultRows();
    if (rows.length === 0) return [];
    return Object.keys(rows[0] as object);
  }

  translateColumn(col: string): string {
    return this.COLUMN_MAP[col] || col;
  }

  getRawJson(): string {
    return JSON.stringify(this.resultRows(), null, 2);
  }
}
